<template>
    <v-dialog v-model="dialog" max-width="640" lazy-validation persistent>
        <template v-slot:activator="{ on, attrs }">
            <slot name="activator" :on="on" :attrs="attrs" :open="openDialog">
                <v-btn color="primary" class="mr-2" :disabled="disabled" v-bind="attrs" v-on="on" @click="openDialog">
                    <v-icon left>mdi-connection</v-icon>
                    <span>Storage</span>
                </v-btn>
            </slot>
        </template>

        <v-card>
            <v-card-title class="flex-nowrap align-start">
                Attach external storage
                <v-spacer></v-spacer>
                <v-btn @click="dialog = false" icon><v-icon>clear</v-icon></v-btn>
            </v-card-title>
            <v-card-text>
                <v-form v-model="mountFormIsValid" ref="mountCreateForm">
                    <v-stepper v-model="activeStep" flat>
                        <v-stepper-header class="elevation-0 mx-12">
                            <v-stepper-step :complete="credentialsSaved" :editable="activeStep > 1" step="1">Credentials</v-stepper-step>
                            <v-divider></v-divider>
                            <v-stepper-step :editable="credentialsSaved" step="2">Configuration</v-stepper-step>
                        </v-stepper-header>

                        <v-stepper-items>
                            <v-stepper-content step="1" class="pa-0">
                                <v-text-field
                                    class="mt-2"
                                    v-model="accessKey"
                                    :disabled="savingCredentials || addingMount"
                                    outlined
                                    :rules="[rules.required]"
                                    dense>
                                    <template v-slot:label>Access key</template>
                                </v-text-field>
                                <v-text-field
                                    v-model="secretKey"
                                    :disabled="savingCredentials || addingMount"
                                    outlined
                                    :append-icon="showSecretKey ? 'visibility' : 'visibility_off'"
                                    :type="showSecretKey ? 'text' : 'password'"
                                    @click:append="showSecretKey = !showSecretKey"
                                    :rules="[rules.required]"
                                    dense>
                                    <template v-slot:label>Secret key</template>
                                </v-text-field>
                            </v-stepper-content>

                            <v-stepper-content step="2" class="pa-0">
                                <v-select
                                    class="mt-2"
                                    v-model="selectedBucket"
                                    :items="bucketOptions"
                                    label="Bucket"
                                    item-text="name"
                                    item-value="id"
                                    :disabled="savingCredentials || addingMount"
                                    outlined
                                    dense
                                    :rules="[rules.required]">
                                    <template v-slot:item="{ item, on, attrs }">
                                        <v-list-item two-line v-bind="attrs" v-on="on">
                                            <v-list-item-content>
                                                <v-list-item-title>{{ item.name }}</v-list-item-title>
                                                <v-list-item-subtitle>{{ item.endpoint }}</v-list-item-subtitle>
                                            </v-list-item-content>
                                        </v-list-item>
                                    </template>
                                </v-select>
                                <v-text-field
                                    v-model="mountPoint"
                                    :disabled="savingCredentials || addingMount"
                                    outlined
                                    hint="This field is required"
                                    :rules="[rules.required, rules.validChars]"
                                    dense>
                                    <template v-slot:label>Mount point</template>
                                </v-text-field>
                                <div class="mb-1">Storage will be mounted under</div>
                                <code class="d-flex rounded py-2 px-3">
                                    /
                                    <b>{{ mountPoint }}</b>
                                </code>
                            </v-stepper-content>
                        </v-stepper-items>
                    </v-stepper>
                </v-form>
            </v-card-text>
            <v-card-actions>
                <v-btn v-if="activeStep === 2 && credentialsSaved" text @click="activeStep = 1" :disabled="addingMount || attachSuccess">Back</v-btn>
                <v-spacer></v-spacer>
                <v-btn
                    v-if="activeStep === 1"
                    text
                    color="primary"
                    :loading="savingCredentials"
                    :disabled="savingCredentials || !accessKey || !secretKey"
                    @click="saveCredentials">
                    Validate and connect
                </v-btn>

                <v-btn v-if="activeStep === 2 && credentialsSaved && attachSuccess" color="success" text>
                    <v-icon>check</v-icon>
                </v-btn>

                <v-btn
                    v-else-if="activeStep === 2 && credentialsSaved"
                    text
                    color="primary"
                    @click="sendPostRequest"
                    :disabled="!mountFormIsValid"
                    :loading="addingMount">
                    Attach
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
export default {
    name: 'SpaceAttachExternalStorageDialog',
    props: {
        spaceId: {
            type: [String, Number],
            default: null
        },
        disabled: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            dialog: false,
            activeStep: 1,
            bucketOptions: [{ id: 'nv-audit-logs-prod', name: 'data', endpoint: 'ZHC-C (HDD)' }],
            selectedBucket: null,
            mountFormIsValid: true,
            mountPoint: '',
            accessKey: '',
            secretKey: '',
            showSecretKey: false,
            savingCredentials: false,
            credentialsSaved: false,
            credentialsSaveSuccess: false,
            credentialsSaveSuccessTimeout: null,
            addingMount: false,
            attachSuccess: false,
            rules: {
                required: value => !!value || 'This field is required',
                validChars: p => /^[a-zA-Z0-9-_]*$/g.test(p) || 'Please use only letters, numbers, underscores and dashes'
            }
        }
    },
    computed: {
        selectedBucketObject() {
            return this.bucketOptions.find(bucket => bucket.id === this.selectedBucket)
        }
    },
    methods: {
        openDialog() {
            this.dialog = true
            this.activeStep = this.credentialsSaved ? 2 : 1
        },
        fakeSaveCredentialsApiCall() {
            return new Promise(resolve => setTimeout(resolve, 1000))
        },
        async saveCredentials() {
            if (!this.accessKey || !this.secretKey || this.savingCredentials) return

            this.credentialsSaveSuccess = false
            this.savingCredentials = true
            await this.fakeSaveCredentialsApiCall()
            this.savingCredentials = false
            this.credentialsSaved = true
            this.credentialsSaveSuccess = true
            this.activeStep = 2

            if (this.credentialsSaveSuccessTimeout) {
                clearTimeout(this.credentialsSaveSuccessTimeout)
            }

            this.credentialsSaveSuccessTimeout = setTimeout(() => {
                this.credentialsSaveSuccess = false
                this.credentialsSaveSuccessTimeout = null
            }, 2000)
        },
        async sendPostRequest() {
            if (this.addingMount || this.attachSuccess) return

            this.addingMount = true
            this.attachSuccess = false

            // Demo only: no backend calls, emit a local item payload.
            this.$emit('attached', {
                amid: Date.now(),
                long_id: this.selectedBucketObject?.name || this.mountPoint,
                quota_gib: 500,
                read_only: false,
                mount_path: `/${this.mountPoint}`,
                bucket_id: this.selectedBucket,
                access_key: this.accessKey,
                secret_key: this.secretKey
            })

            await new Promise(resolve => setTimeout(resolve, 1200))

            this.addingMount = false
            this.attachSuccess = true

            await new Promise(resolve => setTimeout(resolve, 2000))

            this.dialog = false
            this.selectedBucket = null
            this.mountPoint = ''
            this.accessKey = ''
            this.secretKey = ''
            this.showSecretKey = false
            this.activeStep = 1
            this.savingCredentials = false
            this.credentialsSaved = false
            this.credentialsSaveSuccess = false
            this.attachSuccess = false
            if (this.credentialsSaveSuccessTimeout) {
                clearTimeout(this.credentialsSaveSuccessTimeout)
                this.credentialsSaveSuccessTimeout = null
            }
            this.$refs.mountCreateForm.reset()
        }
    },
    watch: {
        accessKey() {
            this.credentialsSaved = false
            this.credentialsSaveSuccess = false
            this.activeStep = 1
            if (this.credentialsSaveSuccessTimeout) {
                clearTimeout(this.credentialsSaveSuccessTimeout)
                this.credentialsSaveSuccessTimeout = null
            }
        },
        secretKey() {
            this.credentialsSaved = false
            this.credentialsSaveSuccess = false
            this.activeStep = 1
            if (this.credentialsSaveSuccessTimeout) {
                clearTimeout(this.credentialsSaveSuccessTimeout)
                this.credentialsSaveSuccessTimeout = null
            }
        }
    }
}
</script>
